import { motion } from "framer-motion";
import { ArrowRight, Clock3 } from "lucide-react";
import { waLink } from "@/lib/site";
import { T, useLang } from "@/lib/i18n";

const ROUTE_IMAGES = [
  "/images/munich.webp",
  "/images/vienna.webp",
  "/images/innsbruck.webp",
  "/images/prague.webp",
  "/images/hallstatt-lake.webp",
  "/images/zell-glacier.webp",
  "/images/berchtesgaden.webp",
  "/images/koenigssee.webp",
];

export default function Routes() {
  const { lang } = useLang();
  const t = T[lang].routes;

  return (
    <section id="routes" data-testid="routes-section" className="relative py-24 lg:py-32 overflow-hidden">
      <img src="/images/routes-bg.webp" alt="" aria-hidden="true" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-[#16110C]/50" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#16110C] via-[#16110C]/25 to-[#16110C]" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="max-w-2xl"
        >
          <p className="font-mono-label text-xs uppercase tracking-[0.28em] text-[#C5A059] mb-4">{t.overline}</p>
          <h2 className="font-serif-display font-semibold tracking-tight text-stone-100 text-3xl sm:text-4xl lg:text-5xl">
            {t.h2a}<em className="not-italic text-[#C5A059]">{t.h2b}</em>
          </h2>
          <p className="mt-5 text-stone-400 leading-relaxed">{t.sub}</p>
        </motion.div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {t.items.map((r, i) => (
            <motion.a
              key={r.to}
              data-testid={`route-card-${i}`}
              href={waLink(`${t.wa} ${r.to}`)}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: (i % 4) * 0.07, ease: "easeOut" }}
              whileHover={{ scale: 1.05 }}
              className="group relative rounded-2xl overflow-hidden border border-stone-800 hover:border-[#C5A059]/40 transition-colors duration-400 min-h-[190px] flex flex-col justify-end"
            >
              <img
                src={ROUTE_IMAGES[i]}
                alt={r.to}
                loading="lazy" decoding="async"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/15" />
              <div className="relative p-5">
                <p className="text-[10px] uppercase tracking-[0.18em] text-white/60 font-semibold">Salzburg</p>
                <p className="mt-1 flex items-center gap-2 font-serif-display font-semibold text-xl text-white">
                  <ArrowRight className="w-4 h-4 shrink-0 text-[#C5A059] transition-transform duration-300 group-hover:translate-x-1" />
                  {r.to}
                </p>
                <div className="mt-3 flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-xs text-white/75">
                    <Clock3 className="w-3.5 h-3.5 text-[#C5A059]" />
                    {r.time}
                  </span>
                  <span className="text-[11px] font-semibold text-[#C5A059] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {t.cta}
                  </span>
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
